import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Button, 
  Box, 
  CircularProgress, 
  Alert,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton,
  Tooltip,
  useTheme,
  InputBase,
  alpha
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuth } from '../context/AuthContext';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

function TeamDashboard() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { authAxios } = useAuth();
    const [teamItems, setTeamItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
  

// Function to fetch news
const fetchTeamItem = async () => {
  setLoading(true);
  setError('');
  try {
    // Using authAxios for consistent error handling
    const response = await authAxios.get('/team');
    setTeamItems(response.data);
    setFilteredItems(response.data);
  } catch (err) {
    console.error("Failed to fetch team members:", err);
    setError(
      err.response?.data?.message || 
      err.message || 
      'Failed to load team items.'
    );
  } finally {
    setLoading(false);
  }
};

// Filter news items based on search query
useEffect(() => {
  if (searchQuery.trim() === '') {
    setFilteredItems(teamItems);
  } else {
    const filtered = teamItems.filter(item => 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredItems(filtered);
  }
}, [searchQuery, teamItems]);

// Fetch news on component mount
useEffect(() => {
  fetchTeamItem();
}, []);

// Handle search input change
const handleSearchChange = (event) => {
  setSearchQuery(event.target.value);
};

// Navigation handlers
const handleAddTeam = () => {
  navigate('/team/add');
};

const handleEditTeam = (id) => {
  navigate(`/team/edit/${id}`);
};

const handleDeleteTeam = async (id) => {
  if (window.confirm('Are you sure you want to delete this team item?')) {
      setError('');
      try {
          // Using authAxios which handles token refreshing automatically
          await authAxios.delete(`/team/${id}`);
          fetchTeamItem(); 
      } catch (err) {
          console.error("Failed to delete team member:", err);
          setError(
              err.response?.data?.message || 
              err.message || 
              'Failed to delete ream item.'
          );
      }
  }
};
const onDragEnd = async (result) => {
  if (!result.destination) return;

  // Don’t reorder while search is active (reordering a subset is wrong)
  if (searchQuery.trim() !== '') {
    setError('Clear search before reordering.');
    return;
  }

  const items = Array.from(teamItems);
  const [moved] = items.splice(result.source.index, 1);
  items.splice(result.destination.index, 0, moved);

  // Optimistic UI
  setTeamItems(items);
  setFilteredItems(items);

  try {
    await authAxios.put('/team/reorder-list', {
      orderedIds: items.map((x) => x.id),
    });
  } catch (err) {
    setError(err.response?.data?.message || 'Failed to save order.');
    fetchTeamItem(); // rollback
  }
};


return (
  <Box sx={{ 
       width: '100%',              // full width of parent
        m: 0,
        px: { xs: 2, md: 3 },       // small padding, but still left-aligned
        py: { xs: 2, md: 4 },
        boxSizing: 'border-box',
      }}>
    <Box sx={{ mb: 3 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Team List
      </Typography>
    </Box>

    {/* Action Bar */}
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      flexWrap: 'wrap',
      gap: 2,
      mb: 3 
    }}>
      {/* Search Bar */}
      <Paper
        component="form"
        sx={{
          p: '2px 4px',
          display: 'flex',
          alignItems: 'center',
          width: { xs: '100%', sm: 300 },
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none'
        }}
      >
        <InputBase
          sx={{ ml: 1, flex: 1 }}
          placeholder="Search team items"
          inputProps={{ 'aria-label': 'search team items' }}
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <IconButton type="button" sx={{ p: '10px' }} aria-label="search">
          <SearchIcon />
        </IconButton>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={handleAddTeam}
          sx={{ 
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)'
          }}
        >
          Add Team
        </Button>
      </Box>
    </Box>

    {loading && <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />}
    {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
    
    {!loading && !error && (
      <>
        {filteredItems.length === 0 ? (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No team items found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery ? 'Try a different search term or' : 'Get started by'} adding a new team item.
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={handleAddTeam}
              sx={{ mt: 2, borderRadius: 2 }}
            >
              Add Team
            </Button>
          </Paper>
        ) : (
          <TableContainer 
            component={Paper} 
            elevation={0}
            sx={{ 
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden'
            }}
          >
            <Table sx={{ minWidth: 650 }} aria-label="team table">
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Image</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <DragDropContext onDragEnd={onDragEnd}>
  <Droppable droppableId="team-table">
    {(dropProvided) => (
      <TableBody ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
        {filteredItems.map((item, index) => (
          <Draggable key={item.id} draggableId={String(item.id)} index={index}>
            {(dragProvided, snapshot) => (
              <TableRow
                ref={dragProvided.innerRef}
                {...dragProvided.draggableProps}
                {...dragProvided.dragHandleProps}
                sx={{
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                  },
                  ...(snapshot.isDragging && {
                    backgroundColor: alpha(theme.palette.primary.main, 0.06),
                  }),
                }}
              >
                <TableCell>
                  {item.image_url ? (
                    <Box
                      component="img"
                      src={item.image_url}
                      alt={item.title}
                      sx={{
                        width: 100,
                        height: 60,
                        objectFit: 'contain',
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 100,
                        height: 60,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 1,
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                      }}
                    >
                      No Image
                    </Box>
                  )}
                </TableCell>

                <TableCell>
                  <Typography variant="subtitle2" fontWeight="medium">
                    {item.name}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {item.title}
                  </Typography>
                </TableCell>

                <TableCell align="right">
                  <Tooltip title="View">
                    <IconButton
                      size="small"
                      sx={{
                        color: theme.palette.info.main,
                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                        mr: 1,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.info.main, 0.2),
                        },
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => handleEditTeam(item.id)}
                      sx={{
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        mr: 1,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.2),
                        },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteTeam(item.id)}
                      sx={{
                        color: theme.palette.error.main,
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.error.main, 0.2),
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            )}
          </Draggable>
        ))}

        {dropProvided.placeholder}
      </TableBody>
    )}
  </Droppable>
</DragDropContext>

            </Table>
          </TableContainer>
        )}
      </>
    )}
  </Box>
);
}

export default TeamDashboard;
